import { useState } from "react";
import { Minus, Plus } from "lucide-react";
import { useLocale } from "use-intl";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

/** Digits with at most one decimal separator; "," and "." both count (German keyboards only have ","). */
const NUMBER_TEXT = /^\d*([.,]\d*)?$/;

function decimalSeparator(locale: string) {
  return new Intl.NumberFormat(locale).formatToParts(1.5).find((p) => p.type === "decimal")?.value ?? ".";
}

/** Big, thumb-friendly number input with − / + buttons. */
export function Stepper({
  value,
  onChange,
  step = 1,
  min = 0,
  max = 9999,
  decimals = 0,
  label,
  suffix,
  className,
  size = "default",
}: {
  value: number | null;
  onChange: (v: number | null) => void;
  step?: number;
  min?: number;
  max?: number;
  decimals?: number;
  label?: string;
  suffix?: string;
  className?: string;
  size?: "default" | "sm";
}) {
  const separator = decimalSeparator(useLocale());
  const clamp = (v: number) => Math.min(max, Math.max(min, Number(v.toFixed(decimals))));
  const format = (v: number | null) => (v == null ? "" : String(v).replace(".", separator));

  // A text input instead of type="number": number inputs report "" for "82," or "82,5"
  // in most browsers, so the comma never reached us. The typed text is kept as is
  // (e.g. "82," while the user is still typing) and only replaced when `value`
  // changes from outside, e.g. through the buttons or the parent.
  const [text, setText] = useState(() => format(value));
  const [shown, setShown] = useState(value);
  if (!Object.is(value, shown)) {
    setShown(value);
    setText(format(value));
  }

  function emit(v: number | null, nextText = format(v)) {
    setShown(v);
    setText(nextText);
    onChange(v);
  }

  function handleInput(raw: string) {
    const typed = raw.trim();
    // Ignore keystrokes that would not form a number (letters, a second separator,
    // a separator in a whole-number field); the input keeps its previous text.
    if (!NUMBER_TEXT.test(typed) || (decimals === 0 && /[.,]/.test(typed))) return;
    const normalized = typed.replace(",", ".");
    if (normalized === "" || normalized === ".") {
      emit(null, typed);
      return;
    }
    const parsed = Number(normalized);
    const v = clamp(parsed);
    // Show the corrected number when it had to be clamped or rounded.
    emit(v, v === parsed ? typed : format(v));
  }

  const btn = size === "sm" ? "icon" : "icon-lg";
  return (
    <div className={cn("flex flex-col gap-1", className)}>
      {label && <span className="text-xs font-medium text-muted-foreground">{label}</span>}
      <div className="flex items-center gap-1 rounded-xl border bg-background p-1">
        <Button
          type="button"
          variant="secondary"
          size={btn}
          className="rounded-lg"
          aria-label="−"
          onClick={() => emit(clamp((value ?? 0) - step))}
        >
          <Minus />
        </Button>
        <div className="relative flex-1">
          <input
            type="text"
            inputMode={decimals ? "decimal" : "numeric"}
            autoComplete="off"
            aria-label={label}
            className={cn(
              "w-full bg-transparent text-center font-semibold tabular-nums outline-none",
              size === "sm" ? "text-base" : "text-xl",
            )}
            value={text}
            onFocus={(e) => e.target.select()}
            onChange={(e) => handleInput(e.target.value)}
          />
          {suffix && (
            <span className="pointer-events-none absolute top-1/2 right-1 -translate-y-1/2 text-xs text-muted-foreground">
              {suffix}
            </span>
          )}
        </div>
        <Button
          type="button"
          variant="secondary"
          size={btn}
          className="rounded-lg"
          aria-label="+"
          onClick={() => emit(clamp((value ?? 0) + step))}
        >
          <Plus />
        </Button>
      </div>
    </div>
  );
}
